import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Mail, Phone, Smartphone, Users } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Card } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import Dashboard from "./dashboard";
import { BusinessUserFilter } from "./businessUserFilter";
import { useGetAllDepartments, useUserInfoSearch } from "../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

interface BusinessUserData {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: string;
  communicationPreferences: string[];
}

interface TransformedBusinessUser {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: string;
  communicationPreferences: string[];
}

interface BusinessUserFilters {
  department: string[];
  role: string[];
  status: string;
}

interface BusinessUserMemberListProps {
  onSubmit: (payload: any) => void;
  onCancel: () => void;
  form?: any;
  formValues: {
    listName: string;
    tags: string[];
    audience: string;
    segment: string;
    listType: string;
  };
  remoteUtilityId: number;
  preSelectedIds?: string[];
  action?: string;
  filters: BusinessUserFilters;
  onFilterChange: (filters: BusinessUserFilters) => void;
}

const ITEMS_PER_PAGE = PAGE_SIZE;

const BusinessUserMemberList: React.FC<BusinessUserMemberListProps> = ({
  onSubmit,
  onCancel,
  form,
  formValues,
  remoteUtilityId,
  preSelectedIds = [],
  action,
  filters,
  onFilterChange,
}) => {
  const { toast } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const { data: businessUserList } = useUserInfoSearch(
    Number(formValues.segment),
    formValues.listType === "0"
      ? "STATIC_BUSINESS_USERS"
      : "DYNAMIC_BUSINESS_USERS",
    searchQuery,
    currentPage,
    ITEMS_PER_PAGE,
    filters.department,
    filters.role,
    filters.status
  );

  // Set pre-selected IDs when in edit mode
  useEffect(() => {
    if (action === "edit" && preSelectedIds.length > 0) {
      setSelectedRowIds(preSelectedIds);
    }
  }, [action, preSelectedIds]);

  // Transform API data to match the table structure
  const transformedData: TransformedBusinessUser[] = useMemo(() => {
    return (
      businessUserList?.result?.map((item: any) => ({
        id: String(item.id),
        name: item.user?.name || "N/A",
        email: item.user?.email || "N/A",
        role: item.roleCode?.[0] || "No Role",
        department:
          item.extraDataMap?.department?.[0]
            ?.toLowerCase()
            .replace(/\s+department/i, "")
            .replace(/\s+/g, "_") || "N/A",
        status:
          item.status?.toLowerCase() === "active"
            ? "active"
            : item.status?.toLowerCase() === "expired"
            ? "inactive"
            : "pending",
        communicationPreferences: ["Email"], // Default - adjust based on actual API data
      })) || []
    );
  }, [businessUserList]);

  // Apply frontend filters
  const filteredData = useMemo(() => {
    let filtered = transformedData;

    if (filters.department.length > 0) {
      filtered = filtered.filter((item) =>
        filters.department.some(
          (dept) => item.department.toLowerCase() === dept.toLowerCase()
        )
      );
    }

    if (filters.role.length > 0) {
      filtered = filtered.filter((item) =>
        filters.role.some(
          (role) => item.role.toLowerCase() === role.toLowerCase()
        )
      );
    }

    return filtered;
  }, [transformedData, filters.department, filters.role]);

  const selectedRows = useMemo(() => {
    return filteredData.filter((row) => selectedRowIds.includes(row.id));
  }, [selectedRowIds, filteredData]);

  const totalItems = businessUserList?.count;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const pagination = useMemo(() => {
    const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    return {
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: ITEMS_PER_PAGE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    };
  }, [currentPage, totalItems, totalPages]);

  const dashboardStats = useMemo(() => {
    const totalAvailable = businessUserList?.count || 0;
    const totalSelected = selectedRows?.length || 0;
    const emailOnly =
      selectedRows?.filter(
        (row) =>
          row.communicationPreferences.length === 1 &&
          row.communicationPreferences.includes("Email")
      ).length || 0;
    const smsOnly =
      selectedRows?.filter(
        (row) =>
          row.communicationPreferences.length === 1 &&
          row.communicationPreferences.includes("SMS")
      ).length || 0;
    const bothChannels =
      selectedRows?.filter((row) => row.communicationPreferences.length === 2)
        .length || 0;

    return [
      {
        title: "Total Available",
        count: totalAvailable,
        icon: <Users className="h-8 w-8 text-blue-500" />,
      },
      {
        title: "Total Selected",
        count: totalSelected,
        icon: <Users className="h-8 w-8 text-blue-500" />,
      },
      {
        title: "Email Only",
        count: emailOnly,
        icon: <Mail className="h-8 w-8 text-green-500" />,
      },
      {
        title: "Phone Only",
        count: smsOnly,
        icon: <Phone className="h-8 w-8 text-orange-500" />,
      },
      {
        title: "Both Channels",
        count: bothChannels,
        icon: <Smartphone className="h-8 w-8 text-red-500" />,
      },
    ];
  }, [selectedRows, businessUserList]);

  const handleApplyFilters = (newFilters: BusinessUserFilters) => {
    onFilterChange(newFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    onFilterChange({
      department: [],
      role: [],
      status: "",
    });
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedRowIds(selectedIds);
  };

  const columns: TableColumn<TransformedBusinessUser>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value}</span>
      ),
    },
    {
      key: "role",
      header: "Role",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-gray-900">{value}</span>
      ),
    },
    {
      key: "department",
      header: "Department",
      sortable: true,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: string) => {
        const statusColors: Record<string, string> = {
          active: "bg-green-100 text-green-800",
          inactive: "bg-gray-100 text-gray-800",
          pending: "bg-yellow-100 text-yellow-800",
        };
        const colorClass =
          statusColors[value.toLowerCase()] || "bg-gray-100 text-gray-800";
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        );
      },
    },
  ];

  // Build and submit the payload - handles both static and dynamic lists
  const handleSubmitClick = async () => {
    // Validate parent form first
    if (form) {
      const isValid = await form.trigger();
      if (!isValid) {
        return;
      }
    }

    const isStaticList = formValues.listType === "0";
    const isDynamicList = formValues.listType === "1";

    // Validation based on list type
    if (isStaticList && selectedRowIds.length === 0) {
      toast({
        title: "No Members Selected",
        description: "Please select at least one member for static list.",
        variant: "destructive",
      });
      return;
    }

    if (isDynamicList) {
      const hasFilters = 
        filters.department.length > 0 || 
        filters.role.length > 0 || 
        filters.status;
      
      if (!hasFilters) {
        toast({
          title: "No Filters Applied",
          description: "Please apply at least one filter for dynamic list.",
          variant: "destructive",
        });
        return;
      }
    }

    // Base payload structure
    const basePayload = {
      remote_utility_id: remoteUtilityId,
      name: formValues.listName,
      audience_type: Number(formValues.audience),
      filter_type: Number(formValues.listType),
      segment: Number(formValues.segment) || undefined,
      tags: formValues.tags,
    };

    let payload: any;

    if (isStaticList) {
      // Static list - include recipient_ids
      payload = {
        ...basePayload,
        recipient_data: {
          recipient_ids: selectedRowIds,
        },
      };
    } else {
      // Dynamic list - include filters as arrays
      const dynamicFilters: any = {};

      // Only include non-empty filters as arrays
      if (filters.department.length > 0) {
        dynamicFilters.department = filters.department;
      }
      if (filters.role.length > 0) {
        dynamicFilters.role = filters.role;
      }
      if (filters.status) {
        dynamicFilters.status = filters.status;
      }

      payload = {
        ...basePayload,
        filters: dynamicFilters,
      };
    }
    if (isStaticList) {
      console.log("Total Recipients Selected:", selectedRowIds.length);
    } else {
      console.log("Filters Applied:", payload.filters);
    }

    // Pass the complete payload to parent
    onSubmit(payload);
  };

  const handleCancelClick = () => {
    onCancel();
  };

  const filterPanel = (
    <BusinessUserFilter
      department={filters.department}
      role={filters.role}
      status={filters.status}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
    />
  );

  const hasActiveFilters = 
    filters.department.length > 0 || 
    filters.role.length > 0 || 
    filters.status;
  
  const activeFilterCount = [
    filters.department.length > 0, 
    filters.role.length > 0,
    !!filters.status,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6 p-1">
      <Card className="mt-2">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {formValues.listType === "0" 
              ? "Select Members (Static List)" 
              : "Configure Filters (Dynamic List)"}
          </h2>
          <p className="text-sm text-gray-500">
            {formValues.listType === "0"
              ? "Select specific recipients to include in this static list"
              : "Set filters to automatically include recipients matching criteria"}
          </p>
        </div>
        <Dashboard statsData={dashboardStats} />
        <div className="mt-2">
          <GenericTable
            data={transformedData || []}
            columns={columns}
            rowKey="id"
            selectable={formValues.listType === "0"}
            selectedRows={selectedRowIds}
            onSelectionChange={handleSelectionChange}
            emptyMessage="No business users found matching your criteria"
            pagination={pagination}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by name, email or role..."
            searchDebounceMs={300}
            filterPanel={filterPanel}
            filters={
              hasActiveFilters ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {activeFilterCount} filter(s) active
                  </span>
                </div>
              ) : null
            }
          />
        </div>
      </Card>
      <div className="flex justify-end">
        <Button variant="default" onClick={handleCancelClick} className="mr-2">
          Cancel
        </Button>
        <Button onClick={handleSubmitClick}>
          {action === "edit" ? "Update" : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default BusinessUserMemberList;